import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { BlueprintGrid, CrossMarker } from "../components/BlueprintGrid";
import { Link } from "react-router-dom";
import { scrollToSection, LOCATION_COORDINATE_META } from "../lib/site";
import { lineMask, EASE } from "../lib/motion";

const HERO_IMG =
    "https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=2000&auto=format&fit=crop";
const LINES = [
    "A TRUSTED NETWORK",
    "FOR RESIDENTIAL",
    "CONSTRUCTION",
    "PROFESSIONALS.",
];

const ProfHero = () => (
    <section
        data-testid="prof-hero"
        className="relative flex min-h-[100svh] flex-col overflow-hidden bg-ink"
    >
        <BlueprintGrid />
        <CrossMarker className="absolute left-10 top-28 hidden text-white/25 md:block" />

        <div className="relative z-10 mx-auto flex w-full max-w-[1600px] flex-1 items-center px-6 pb-16 pt-36 md:px-10 lg:px-16">
            <div className="grid w-full items-center gap-14 lg:grid-cols-12">
                <div className="lg:col-span-6">
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
                        className="flex items-center gap-3 font-mono text-[13px] tracking-[0.3em] text-mist"
                    >
                        <span className="h-1.5 w-1.5 bg-accent" />
                        FOR PROFESSIONALS
                    </motion.p>

                    <h1
                        data-testid="prof-hero-headline"
                        className="mt-6 font-display text-[1.75rem] min-[340px]:text-[clamp(2.1rem,4.2vw,4.2rem)] font-bold leading-[1.12] text-white"
                    >
                        {LINES.map((line, i) => (
                            <span
                                key={line}
                                className="-mb-[0.08em] block overflow-hidden pb-[0.08em]"
                            >
                                <motion.span
                                    className="block"
                                    variants={lineMask}
                                    initial="hidden"
                                    animate="show"
                                    custom={i}
                                >
                                    {i === 3 ? (
                                        <>
                                            PROFESSIONALS
                                            <span className="text-accent">
                                                .
                                            </span>
                                        </>
                                    ) : (
                                        line
                                    )}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: EASE, delay: 0.8 }}
                        className="mt-8 max-w-lg text-[16px] leading-relaxed text-mist md:text-base"
                    >
                        BuildEase connects homeowners with relevant
                        construction professionals through transparent,
                        project-aware matching.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: EASE, delay: 0.95 }}
                        className="mt-10 flex flex-wrap items-center gap-x-9 gap-y-5"
                    >
                        <Link
                            to="/join-the-pilot"
                            data-testid="prof-hero-apply"
                            className="inline-flex items-center gap-2 bg-accent px-7 py-4 text-sm font-semibold tracking-wide text-white transition-colors duration-300 hover:bg-[#ff7026]"
                        >
                            Apply to Join
                            <ArrowUpRight className="h-4 w-4" />
                        </Link>
                        <a
                            href="#workspace"
                            data-testid="prof-hero-see-workspace"
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToSection("#workspace");
                            }}
                            className="group inline-flex items-center gap-2 text-sm tracking-wide text-white"
                        >
                            See the Workspace
                            <span className="text-accent transition-transform duration-300 group-hover:translate-y-1">
                                ↓
                            </span>
                        </a>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.1, ease: EASE, delay: 0.4 }}
                    className="lg:col-span-6"
                >
                    <div
                        data-testid="prof-hero-figure"
                        className="group relative overflow-hidden border border-white/10 bg-surface"
                    >
                        <div className="relative aspect-[4/3] md:aspect-[16/10]">
                            <img
                                src={HERO_IMG}
                                alt="Construction professional drafting technical drawings by hand"
                                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.03]"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-ink/25" />
                            <div className="blueprint-bg absolute inset-0 opacity-25" />
                            <p className="absolute left-5 top-5 flex items-center gap-2.5 font-mono text-[13px] tracking-[0.22em] text-white/75">
                                <span className="h-1 w-1 bg-accent" />
                                FIG. PR.01 — IN PRACTICE
                            </p>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center justify-between font-mono text-[13px] tracking-[0.2em] text-cad">
                        <span>TECHNICAL DOCUMENTATION — FIELD STUDY</span>
                        <span>01 : 100</span>
                    </div>
                </motion.div>
            </div>
        </div>

        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="relative z-10 border-t border-white/10"
            data-testid="prof-metadata-bar"
        >
            <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-6 px-6 py-4 font-mono text-[13px] tracking-[0.24em] text-mist/80 md:px-10 lg:px-16">
                <span className="hidden sm:block">BUILD INTELLIGENCE</span>
                <span data-testid="prof-stage-counter">FOR PROFESSIONALS</span>
                <span>SOUTH AFRICA</span>
                <span className="hidden md:block">{LOCATION_COORDINATE_META}</span>
            </div>
        </motion.div>
    </section>
);

export default ProfHero;
